import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { MultiSelect } from '@/components/ui/MultiSelect'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { Textarea } from '@/components/ui/Textarea'
import { leadsApi } from '@/features/leads/api/leadsApi'
import { clientTypesApi, stagesApi, statesApi } from '@/lib/api/clientDictionariesApi'
import {
  actualitiesApi,
  buildingsApi,
  conditionsApi,
  liveComplexesApi,
  propertyTypesApi,
  repairsApi,
  roomsApi,
} from '@/lib/api/dictionariesApi'
import { citiesApi } from '@/lib/api/geolocateApi'
import { districtsApi, streetsApi, subwayApi } from '@/lib/api/streetsApi'
import { toastService } from '@/lib/toastService'
import { Checkbox, Flex, Grid, HStack, Stack, VStack } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { arendatorsApi } from '../api/arendatorsApi'
import { getAmentiesOptions, getWallTypeOptions } from '../constants/arendatorEnums'

/**
 * Arendator Modal Component
 * Complete form for creating/editing arendators with all fields from schema
 */
export function ArendatorModal({ isOpen, onClose, arendator, onSuccess }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!arendator
  const [fullArendatorData, setFullArendatorData] = useState(null)
  const [isLoadingFull, setIsLoadingFull] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      // Basic Information
      name: '',
      phone_first: '',
      phone_second: '',
      email: '',
      commission: '',
      comment: '',

      // Organizational
      type_state: null,
      type_client: null,
      type_stage: null,
      actuality: null,
      rieltor: [],
      loyality: [],

      // Geolocation
      city: null,
      district_obj: [],
      street_obj: null,
      streets: [],
      subways: [],

      // Search Criteria
      type_building_data: [],
      room: [],
      repairs: [],

      // Property Characteristics
      rooms_from: '',
      rooms_to: '',
      floors_from: '',
      floors_to: '',
      floor: '',
      floor_to: '',
      type_building: null,
      condition: null,
      conditions: [],
      buildings: [],
      wall_type: '',

      // Areas
      area_from: '',
      area_to: '',
      total_area: '',
      area_living_room: '',
      area_kitchen: '',

      // Land/Lots
      land_from: '',
      land_to: '',
      lot_from: '',
      lot_to: '',
      apartment_count_from: '',
      apartment_count_to: '',

      // Price and Complex
      price_from: '',
      price_to: '',
      live_complex: [],
      amenties: '',

      // Dates and persons
      date_term: '',
      review_date: '',
      call_date: '',
      number_of_persons: '',

      // Client Info (private)
      client_name: '',
      client_phone: '',
      client_comment: '',
      is_public: true,
      is_cancel: false,

      // Owner Info
      name_owner: '',
      phone_owner: '',
      phone_owner_plus: '',
      email_owner: '',
    },
  })

  // Load full arendator data if arendator comes from autocomplete (has client_name but not name)
  useEffect(() => {
    const loadFullData = async () => {
      if (isOpen && arendator && arendator.id && arendator.client_name && !arendator.name) {
        // This arendator is from autocomplete, load full data
        setIsLoadingFull(true)
        try {
          const response = await arendatorsApi.getById(arendator.id)
          setFullArendatorData(response.data)
        } catch (error) {
          console.error('Failed to load full arendator data:', error)
          toastService.error({
            title: t('arendators.loadError'),
            description: error.message,
          })
          setFullArendatorData(null)
        } finally {
          setIsLoadingFull(false)
        }
      } else {
        setFullArendatorData(null)
      }
    }
    loadFullData()
  }, [isOpen, arendator, t])

  // Reset form when arendator changes or modal closes
  useEffect(() => {
    const arendatorData = fullArendatorData || arendator

    if (isOpen && arendatorData) {
      reset({
        // Basic Information
        name: arendatorData.name || arendatorData.client_name || '',
        phone_first: arendatorData.phone_first || arendatorData.client_phone || '',
        phone_second: arendatorData.phone_second || '',
        email: arendatorData.email || '',
        commission: arendatorData.commission || '',
        comment: arendatorData.comment || '',

        // Organizational
        type_state: arendatorData.type_state || null,
        type_client: arendatorData.type_client || null,
        type_stage: arendatorData.type_stage || null,
        actuality: arendatorData.actuality || null,
        rieltor: arendatorData.rieltor || [],
        loyality: arendatorData.loyality || [],

        // Geolocation
        city: arendatorData.city || null,
        district_obj: arendatorData.district_obj || [],
        street_obj: arendatorData.street_obj || null,
        streets: arendatorData.streets || [],
        subways: arendatorData.subways || [],

        // Search Criteria
        type_building_data: arendatorData.type_building_data || [],
        room: arendatorData.room || [],
        repairs: arendatorData.repairs || [],

        // Property Characteristics
        rooms_from: arendatorData.rooms_from || '',
        rooms_to: arendatorData.rooms_to || '',
        floors_from: arendatorData.floors_from || '',
        floors_to: arendatorData.floors_to || '',
        floor: arendatorData.floor || '',
        floor_to: arendatorData.floor_to || '',
        type_building: arendatorData.type_building || null,
        condition: arendatorData.condition || null,
        conditions: arendatorData.conditions || [],
        buildings: arendatorData.buildings || [],
        wall_type: arendatorData.wall_type || '',

        // Areas
        area_from: arendatorData.area_from || '',
        area_to: arendatorData.area_to || '',
        total_area: arendatorData.total_area || '',
        area_living_room: arendatorData.area_living_room || '',
        area_kitchen: arendatorData.area_kitchen || '',

        // Land/Lots
        land_from: arendatorData.land_from || '',
        land_to: arendatorData.land_to || '',
        lot_from: arendatorData.lot_from || '',
        lot_to: arendatorData.lot_to || '',
        apartment_count_from: arendatorData.apartment_count_from || '',
        apartment_count_to: arendatorData.apartment_count_to || '',

        // Price and Complex
        price_from: arendatorData.price_from || '',
        price_to: arendatorData.price_to || '',
        live_complex: arendatorData.live_complex || [],
        amenties: arendatorData.amenties || '',

        // Dates and persons
        date_term: arendatorData.date_term ? arendatorData.date_term.split('T')[0] : '',
        review_date: arendatorData.review_date ? arendatorData.review_date.split('T')[0] : '',
        call_date: arendatorData.call_date ? arendatorData.call_date.split('T')[0] : '',
        number_of_persons: arendatorData.number_of_persons || '',

        // Client Info (private)
        client_name: arendatorData.client_name || '',
        client_phone: arendatorData.client_phone || '',
        client_comment: arendatorData.client_comment || '',
        is_public: arendatorData.is_public !== undefined ? arendatorData.is_public : true,
        is_cancel: arendatorData.is_cancel || false,

        // Owner Info
        name_owner: arendatorData.name_owner || '',
        phone_owner: arendatorData.phone_owner || '',
        phone_owner_plus: arendatorData.phone_owner_plus || '',
        email_owner: arendatorData.email_owner || '',
      })
    } else if (isOpen) {
      // Reset to defaults when creating new
      reset()
    }
  }, [isOpen, arendator, fullArendatorData, reset])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      // Transform data: convert numbers, extract IDs from arrays/objects, handle nulls
      const transformed = {
        // Basic Information
        name: data.name || null,
        phone_first: data.phone_first || null,
        phone_second: data.phone_second || null,
        email: data.email || null,
        commission: data.commission || null,
        comment: data.comment || null,

        // Organizational (single selects - extract ID)
        type_state: data.type_state || null,
        type_client: data.type_client || null,
        type_stage: data.type_stage || null,
        actuality: data.actuality || null,

        // Multi-selects (extract IDs from array)
        rieltor: Array.isArray(data.rieltor) ? data.rieltor.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        loyality: Array.isArray(data.loyality) ? data.loyality.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],

        // Geolocation
        city: data.city || null,
        district_obj: Array.isArray(data.district_obj) ? data.district_obj.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        street_obj: data.street_obj || null,
        streets: Array.isArray(data.streets) ? data.streets.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        subways: Array.isArray(data.subways) ? data.subways.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],

        // Search Criteria
        type_building_data: Array.isArray(data.type_building_data) ? data.type_building_data.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        room: Array.isArray(data.room) ? data.room.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        repairs: Array.isArray(data.repairs) ? data.repairs.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],

        // Property Characteristics (numbers)
        rooms_from: data.rooms_from ? Number(data.rooms_from) : null,
        rooms_to: data.rooms_to ? Number(data.rooms_to) : null,
        floors_from: data.floors_from ? Number(data.floors_from) : null,
        floors_to: data.floors_to ? Number(data.floors_to) : null,
        floor: data.floor ? Number(data.floor) : null,
        floor_to: data.floor_to ? Number(data.floor_to) : null,
        type_building: data.type_building || null,
        condition: data.condition || null,
        conditions: Array.isArray(data.conditions) ? data.conditions.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        buildings: Array.isArray(data.buildings) ? data.buildings.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        wall_type: data.wall_type || null,

        // Areas (numbers)
        area_from: data.area_from ? Number(data.area_from) : null,
        area_to: data.area_to ? Number(data.area_to) : null,
        total_area: data.total_area ? Number(data.total_area) : null,
        area_living_room: data.area_living_room || null,
        area_kitchen: data.area_kitchen ? Number(data.area_kitchen) : null,

        // Land/Lots (numbers)
        land_from: data.land_from ? Number(data.land_from) : null,
        land_to: data.land_to ? Number(data.land_to) : null,
        lot_from: data.lot_from ? Number(data.lot_from) : null,
        lot_to: data.lot_to ? Number(data.lot_to) : null,
        apartment_count_from: data.apartment_count_from ? Number(data.apartment_count_from) : null,
        apartment_count_to: data.apartment_count_to ? Number(data.apartment_count_to) : null,

        // Price and Complex
        price_from: data.price_from ? Number(data.price_from) : null,
        price_to: data.price_to ? Number(data.price_to) : null,
        live_complex: Array.isArray(data.live_complex) ? data.live_complex.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        amenties: data.amenties || null,

        // Dates
        date_term: data.date_term || null,
        review_date: data.review_date || null,
        call_date: data.call_date || null,
        number_of_persons: data.number_of_persons ? Number(data.number_of_persons) : null,

        // Client Info (private)
        client_name: data.client_name || null,
        client_phone: data.client_phone || null,
        client_comment: data.client_comment || null,
        is_public: data.is_public !== undefined ? data.is_public : true,
        is_cancel: data.is_cancel || false,

        // Owner Info
        name_owner: data.name_owner || null,
        phone_owner: data.phone_owner || null,
        phone_owner_plus: data.phone_owner_plus || null,
        email_owner: data.email_owner || null,
      }

      if (isEdit) {
        return arendatorsApi.update(arendator.id, transformed)
      } else {
        return arendatorsApi.create(transformed)
      }
    },
    onSuccess: (response) => {
      toastService.success({
        title: isEdit ? t('arendators.updateSuccess') : t('arendators.createSuccess'),
      })
      queryClient.invalidateQueries({ queryKey: ['arendators'] })
      if (onSuccess) {
        onSuccess(response.data)
      }
      handleClose()
    },
    onError: (error) => {
      toastService.error({
        title: isEdit ? t('arendators.updateError') : t('arendators.createError'),
        description: error.message,
      })
    },
  })

  const onSubmit = (data) => {
    saveMutation.mutate(data)
  }

  const handleClose = () => {
    if (!saveMutation.isPending) {
      reset()
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Header>
        {isEdit ? t('arendators.editArendator') : t('arendators.createArendator')}
      </Modal.Header>
      <Modal.Body>
        {isLoadingFull ? (
          <Flex justify="center" align="center" minH="300px">
            <Spinner size="lg" />
          </Flex>
        ) : (
          <form id="arendator-form" onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={4} align="stretch">
              {/* Contact Information */}
              <Fieldset>
                <Fieldset.Legend>{t('arendators.contactInfo')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Input
                      label={`${t('arendators.name')} *`}
                      placeholder={t('arendators.namePlaceholder')}
                      error={errors.name?.message}
                      {...register('name', { required: t('validation.required') })}
                    />
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        label={t('arendators.phoneFirst')}
                        placeholder="+380XXXXXXXXX"
                        error={errors.phone_first?.message}
                        {...register('phone_first')}
                      />
                      <Input
                        label={t('arendators.phoneSecond')}
                        placeholder="+380XXXXXXXXX"
                        error={errors.phone_second?.message}
                        {...register('phone_second')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="email"
                        label={t('arendators.email')}
                        placeholder="email@example.com"
                        error={errors.email?.message}
                        {...register('email')}
                      />
                      <Input
                        label={t('arendators.commission')}
                        placeholder="5%"
                        error={errors.commission?.message}
                        {...register('commission')}
                      />
                    </Grid>
                    <Textarea
                      label={t('arendators.comment')}
                      placeholder={t('arendators.commentPlaceholder')}
                      rows={3}
                      error={errors.comment?.message}
                      {...register('comment')}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Organizational */}
              <Fieldset>
                <Fieldset.Legend>{t('arendators.clientInfo')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Controller
                        name="type_state"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('arendators.typeState')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await statesApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.type_state?.message}
                          />
                        )}
                      />
                      <Controller
                        name="type_client"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('arendators.typeClient')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await clientTypesApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.type_client?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Controller
                        name="type_stage"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('arendators.typeStage')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await stagesApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.type_stage?.message}
                          />
                        )}
                      />
                      <Controller
                        name="actuality"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('arendators.actuality')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await actualitiesApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.actuality?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Controller
                      name="rieltor"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('arendators.rieltor')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await leadsApi.getRealtors({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.rieltor?.message}
                        />
                      )}
                    />
                    <Controller
                      name="loyality"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('arendators.loyality')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await leadsApi.getLoyalties({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.loyality?.message}
                        />
                      )}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Geolocation */}
              <Fieldset>
                <Fieldset.Legend>{t('arendators.geolocation')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <AutocompleteSelect
                          label={t('arendators.city')}
                          placeholder={t('common.select')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await citiesApi.getAutocomplete({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.city?.message}
                        />
                      )}
                    />
                    <Controller
                      name="district_obj"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('arendators.districtObj')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await districtsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.district_obj?.message}
                        />
                      )}
                    />
                    <Controller
                      name="street_obj"
                      control={control}
                      render={({ field }) => (
                        <AutocompleteSelect
                          label={t('arendators.streetObj')}
                          placeholder={t('common.select')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await streetsApi.getAutocomplete({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.street_obj?.message}
                        />
                      )}
                    />
                    <Controller
                      name="streets"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('arendators.streets')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await streetsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.streets?.message}
                        />
                      )}
                    />
                    <Controller
                      name="subways"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('arendators.subways')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await subwayApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.subways?.message}
                        />
                      )}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Search Criteria */}
              <Fieldset>
                <Fieldset.Legend>{t('arendators.searchCriteria')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Controller
                      name="type_building_data"
                      control={control}
                      rules={{ required: t('validation.required') }}
                      render={({ field }) => (
                        <MultiSelect
                          label={`${t('arendators.typeBuildingData')} *`}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await propertyTypesApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.type_building_data?.message}
                        />
                      )}
                    />
                    <Controller
                      name="room"
                      control={control}
                      rules={{ required: t('validation.required') }}
                      render={({ field }) => (
                        <MultiSelect
                          label={`${t('arendators.room')} *`}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await roomsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.room?.message}
                        />
                      )}
                    />
                    <Controller
                      name="repairs"
                      control={control}
                      rules={{ required: t('validation.required') }}
                      render={({ field }) => (
                        <MultiSelect
                          label={`${t('arendators.repairs')} *`}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await repairsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.repairs?.message}
                        />
                      )}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Property Requirements */}
              <Fieldset>
                <Fieldset.Legend>{t('arendators.propertyRequirements')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('arendators.roomsFrom')}
                        placeholder="1"
                        error={errors.rooms_from?.message}
                        {...register('rooms_from')}
                      />
                      <Input
                        type="number"
                        label={t('arendators.roomsTo')}
                        placeholder="3"
                        error={errors.rooms_to?.message}
                        {...register('rooms_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('arendators.floorsFrom')}
                        placeholder="1"
                        error={errors.floors_from?.message}
                        {...register('floors_from')}
                      />
                      <Input
                        type="number"
                        label={t('arendators.floorsTo')}
                        placeholder="10"
                        error={errors.floors_to?.message}
                        {...register('floors_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('arendators.floor')}
                        placeholder="5"
                        error={errors.floor?.message}
                        {...register('floor')}
                      />
                      <Input
                        type="number"
                        label={t('arendators.floorTo')}
                        placeholder="9"
                        error={errors.floor_to?.message}
                        {...register('floor_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Controller
                        name="type_building"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('arendators.typeBuilding')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await buildingsApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.type_building?.message}
                          />
                        )}
                      />
                      <Controller
                        name="condition"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('arendators.condition')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await conditionsApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.condition?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Controller
                      name="conditions"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('arendators.conditions')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await conditionsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.conditions?.message}
                        />
                      )}
                    />
                    <Controller
                      name="buildings"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('arendators.buildings')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await buildingsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.buildings?.message}
                        />
                      )}
                    />
                    <Select
                      label={t('arendators.wallType')}
                      placeholder={t('common.select')}
                      error={errors.wall_type?.message}
                      {...register('wall_type')}
                    >
                      {getWallTypeOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Area Information */}
              <Fieldset>
                <Fieldset.Legend>{t('arendators.areaInfo')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('arendators.areaFrom')}
                        placeholder="50"
                        error={errors.area_from?.message}
                        {...register('area_from')}
                      />
                      <Input
                        type="number"
                        label={t('arendators.areaTo')}
                        placeholder="100"
                        error={errors.area_to?.message}
                        {...register('area_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('arendators.totalArea')}
                        placeholder="75"
                        error={errors.total_area?.message}
                        {...register('total_area')}
                      />
                      <Input
                        label={t('arendators.areaLivingRoom')}
                        placeholder="50"
                        error={errors.area_living_room?.message}
                        {...register('area_living_room')}
                      />
                      <Input
                        type="number"
                        label={t('arendators.areaKitchen')}
                        placeholder="12"
                        error={errors.area_kitchen?.message}
                        {...register('area_kitchen')}
                      />
                    </Grid>
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Land/Lots */}
              <Fieldset>
                <Fieldset.Legend>{t('arendators.landInfo')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('arendators.landFrom')}
                        placeholder="5"
                        error={errors.land_from?.message}
                        {...register('land_from')}
                      />
                      <Input
                        type="number"
                        label={t('arendators.landTo')}
                        placeholder="10"
                        error={errors.land_to?.message}
                        {...register('land_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('arendators.lotFrom')}
                        placeholder="100"
                        error={errors.lot_from?.message}
                        {...register('lot_from')}
                      />
                      <Input
                        type="number"
                        label={t('arendators.lotTo')}
                        placeholder="200"
                        error={errors.lot_to?.message}
                        {...register('lot_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('arendators.apartmentCountFrom')}
                        placeholder="50"
                        error={errors.apartment_count_from?.message}
                        {...register('apartment_count_from')}
                      />
                      <Input
                        type="number"
                        label={t('arendators.apartmentCountTo')}
                        placeholder="100"
                        error={errors.apartment_count_to?.message}
                        {...register('apartment_count_to')}
                      />
                    </Grid>
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Price and Complex */}
              <Fieldset>
                <Fieldset.Legend>{t('arendators.priceAndComplex')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('arendators.priceFrom')}
                        placeholder="50000"
                        error={errors.price_from?.message}
                        {...register('price_from')}
                      />
                      <Input
                        type="number"
                        label={t('arendators.priceTo')}
                        placeholder="100000"
                        error={errors.price_to?.message}
                        {...register('price_to')}
                      />
                    </Grid>
                    <Controller
                      name="live_complex"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('arendators.liveComplex')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await liveComplexesApi.getAutocomplete({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.live_complex?.message}
                        />
                      )}
                    />
                    <Select
                      label={t('arendators.amenties')}
                      placeholder={t('common.select')}
                      error={errors.amenties?.message}
                      {...register('amenties')}
                    >
                      {getAmentiesOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Dates and Persons */}
              <Fieldset>
                <Fieldset.Legend>{t('arendators.datesAndPersons')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                      <Input
                        type="date"
                        label={t('arendators.dateTerm')}
                        error={errors.date_term?.message}
                        {...register('date_term')}
                      />
                      <Input
                        type="date"
                        label={t('arendators.reviewDate')}
                        error={errors.review_date?.message}
                        {...register('review_date')}
                      />
                      <Input
                        type="date"
                        label={t('arendators.callDate')}
                        error={errors.call_date?.message}
                        {...register('call_date')}
                      />
                    </Grid>
                    <Input
                      type="number"
                      label={t('arendators.numberOfPersons')}
                      placeholder="1"
                      error={errors.number_of_persons?.message}
                      {...register('number_of_persons')}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Client Info (Private) */}
              <Fieldset>
                <Fieldset.Legend>{t('arendators.clientInfoPrivate')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        label={t('arendators.clientName')}
                        placeholder={t('arendators.namePlaceholder')}
                        error={errors.client_name?.message}
                        {...register('client_name')}
                      />
                      <Input
                        label={t('arendators.clientPhone')}
                        placeholder="+380XXXXXXXXX"
                        error={errors.client_phone?.message}
                        {...register('client_phone')}
                      />
                    </Grid>
                    <Textarea
                      label={t('arendators.clientComment')}
                      placeholder={t('arendators.commentPlaceholder')}
                      rows={2}
                      error={errors.client_comment?.message}
                      {...register('client_comment')}
                    />
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Checkbox {...register('is_public')}>
                        {t('arendators.isPublic')}
                      </Checkbox>
                      <Checkbox {...register('is_cancel')}>
                        {t('arendators.isCancel')}
                      </Checkbox>
                    </Grid>
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Owner Info */}
              <Fieldset>
                <Fieldset.Legend>{t('arendators.ownerInfo')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Input
                      label={t('arendators.nameOwner')}
                      placeholder={t('arendators.namePlaceholder')}
                      error={errors.name_owner?.message}
                      {...register('name_owner')}
                    />
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        label={t('arendators.phoneOwner')}
                        placeholder="+380XXXXXXXXX"
                        error={errors.phone_owner?.message}
                        {...register('phone_owner')}
                      />
                      <Input
                        label={t('arendators.phoneOwnerPlus')}
                        placeholder="+380XXXXXXXXX"
                        error={errors.phone_owner_plus?.message}
                        {...register('phone_owner_plus')}
                      />
                    </Grid>
                    <Input
                      type="email"
                      label={t('arendators.emailOwner')}
                      placeholder="owner@example.com"
                      error={errors.email_owner?.message}
                      {...register('email_owner')}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>
            </VStack>
          </form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <HStack justify="flex-end" spacing={ 3 }>
          <Button
            variant="outline"
            onClick={handleClose}
            isDisabled={saveMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="arendator-form"
            isLoading={saveMutation.isPending}
          >
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  )
}

export default ArendatorModal
